import {
  CircleCheck,
  CreditCard,
  FileText,
  Inbox,
  LayoutDashboard,
  Package,
  PackagePlus,
  CalendarDays,
  Palette,
  Plug,
  Repeat,
  Settings,
  ShoppingCart,
  TextCursorInput,
  Ticket,
  UserCheck,
  CalendarPlus,
  Users,
} from 'lucide-react';

import { hasOneTimePrice, hasRecurringPrice } from '@dashboard/services/getBillingModel';

import { getOfferCopy } from './scenes/PageProduct/components/OrderBumps/offerCopy';

import PageAfterPayment, { getAfterPaymentSections } from './scenes/PageAfterPayment';
import PageCheckout, { getCheckoutSections } from './scenes/PageCheckout/PageCheckout';
import PageDesign from './scenes/PageDesign';
import PageEvent, { getEventSections } from './scenes/PageEvent/PageEvent';
import PageFields, { getFieldsSections } from './scenes/PageFields/PageFields';
import PageForm, { getFormSections } from './scenes/PageForm/PageForm';
import PageOrderBumps, { getOrderBumpsTabPath } from './scenes/PageOrderBumps/PageOrderBumps';
import PageProduct, { getProductSections } from './scenes/PageProduct/PageProduct';
import { getSettingsSections } from './scenes/PageSettings/PageSettings';
import { getIntegrationsSections, PageIntegrations } from './scenes/PageTracking';
import { editorTabLabel } from './pageEditTabs';

const fieldsTab = page => ({
  path: 'fields',
  label: editorTabLabel('fields', page.type),
  icon: TextCursorInput,
  component: PageFields,
});

export const getPageTabs = page => [
  ...(page.type === 'checkout'
    ? [{ path: 'product', label: 'Product', icon: Package, component: PageProduct }]
    : []),
  ...(page.type === 'event'
    ? [{ path: 'event', label: 'Event', icon: CalendarDays, component: PageEvent }]
    : []),
  ...(page.type === 'checkout' || page.type === 'event'
    ? [
        {
          path: getOrderBumpsTabPath(page),
          label: getOfferCopy(page).Plural,
          icon: PackagePlus,
          component: PageOrderBumps,
          flat: true,
          badge: 'New',
        },
        fieldsTab(page),
        { path: 'checkout', label: 'Checkout', icon: ShoppingCart, component: PageCheckout },
      ]
    : []),
  ...(page.type === 'form'
    ? [fieldsTab(page), { path: 'form', label: 'Details', icon: FileText, component: PageForm }]
    : []),
  { path: 'design', label: 'Theme', icon: Palette, component: PageDesign },
  {
    path: 'after-payment',
    label: page.type === 'form' ? 'After submission' : 'After payment',
    icon: CircleCheck,
    component: PageAfterPayment,
  },
  { path: 'integrations', label: 'Integrations', icon: Plug, component: PageIntegrations },
  // Settings opens the settings modal over the current screen, so it is a plain link.
  { path: 'settings', label: 'Settings', icon: Settings, flat: true },
];

const SECTIONS_BY_TAB = {
  product: getProductSections,
  event: getEventSections,
  checkout: getCheckoutSections,
  form: getFormSections,
  fields: getFieldsSections,
  'after-payment': getAfterPaymentSections,
  integrations: getIntegrationsSections,
  settings: getSettingsSections,
};

// Sections that moved tab keep their old URL working: bookmarks, help-center articles
// and support replies still link there.
// The first match wins, so a whole retired tab comes after its sections.
export const MOVED_SECTIONS = [
  { from: 'checkout/limits', to: 'settings/limits' },
  { from: 'checkout/dynamic-info', to: 'settings/dynamic-info' },
  { from: 'form/limits', to: 'settings/limits' },
  { from: 'checkout/checkout-fields', to: 'fields/form-fields' },
  { from: 'form/fields', to: 'fields/form-fields' },
  { from: 'checkout/discount-code', to: 'fields/form-fields' },
  { from: 'form/title', to: 'fields/form-fields' },
  { from: 'fields/attendees', to: 'fields/attendee-questions' },
  { from: 'group-registration/setup', to: 'fields/attendee-questions' },
  { from: 'group-registration/guest-questions', to: 'fields/attendee-questions' },
  { from: 'group-registration', to: 'fields/attendee-questions' },
  { from: 'emails/email-confirmation', to: 'after-payment/email-confirmation' },
  { from: 'emails/invoices', to: 'settings/invoices' },
  { from: 'emails/email-notifications', to: 'settings/email-notifications' },
  { from: 'emails', to: 'settings' },
  { from: 'tracking/tracking-pixels', to: 'settings/tracking-pixels' },
  { from: 'tracking/integrations', to: 'integrations/integrations' },
  { from: 'settings/integrations', to: 'integrations/integrations' },
  { from: 'tracking', to: 'settings' },
  { from: 'after-payment/invoices', to: 'settings/invoices' },
  { from: 'after-payment/email-notifications', to: 'settings/email-notifications' },
  { from: 'after-payment/integrations', to: 'integrations/integrations' },
  { from: 'after-payment/ticket-qr-codes', to: 'settings/ticket-qr-codes' },
];

// These change nothing a customer sees, so the editor drops the preview for them.
const TABS_WITHOUT_PREVIEW = ['integrations', 'settings'];

export function hasPreview(pathname, pageId) {
  const base = `/pages/${pageId}/customize/`;
  return !TABS_WITHOUT_PREVIEW.some(tab => pathname.startsWith(`${base}${tab}`));
}

export function getEditorNavigation({ page, seller }) {
  return getPageTabs(page).map(({ path, label, icon, flat, badge }) => {
    const getSections = SECTIONS_BY_TAB[path];
    const sections = getSections
      ? getSections({ page, seller, queryClient: null })
          .filter(section => !section.hidden)
          .map(section => ({ path: section.path.replace(/^\//, ''), label: section.label }))
      : [];
    return { path, label, icon, flat, badge, sections };
  });
}

export function getOverviewScreens(page) {
  if (page.type === 'event') {
    return [
      { path: 'overview', label: 'Summary', icon: LayoutDashboard },
      { path: 'bookings', label: 'Bookings', icon: Ticket },
      {
        path: 'attendees',
        label: page.eventDetails?.attendeeDetails?.enabled ? 'Attendees' : 'Guests',
        icon: UserCheck,
      },
      { path: 'add-booking', label: 'Add booking', icon: CalendarPlus },
    ];
  }
  if (page.type === 'form') {
    return [
      { path: 'overview', label: 'Summary', icon: LayoutDashboard },
      { path: 'submissions', label: 'Submissions', icon: Inbox },
      { path: 'leads', label: 'Leads', icon: Users },
    ];
  }
  return [
    { path: 'overview', label: 'Summary', icon: LayoutDashboard },
    ...(hasOneTimePrice(page.product)
      ? [{ path: 'payments', label: 'Payments', icon: CreditCard }]
      : []),
    ...(hasRecurringPrice(page.product)
      ? [{ path: 'subscriptions', label: 'Subscriptions', icon: Repeat }]
      : []),
    { path: 'customers', label: 'Customers', icon: Users },
  ];
}
